/**
 * Module dependencies.
 */

import { Editor, useEditorState } from '@tiptap/react';
import {
  Bold,
  CheckSquare,
  Code,
  Code2,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  Strikethrough,
  Table,
  Undo2
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LinkPopover } from './link-popover';
import { ToolButton, ToolDivider } from './tool-button';

/**
 * `Props` type.
 */

type Props = {
  editor: Editor;
  linkOpen: boolean;
  onLinkOpenChange: (open: boolean) => void;
  onPickImage: () => void;
};

/**
 * Export `EditorToolbar` component.
 */

export function EditorToolbar({ editor, linkOpen, onLinkOpenChange, onPickImage }: Props): JSX.Element {
  const [translate] = useTranslation();
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      blockquote: current.isActive('blockquote'),
      bold: current.isActive('bold'),
      bulletList: current.isActive('bulletList'),
      canRedo: current.can().redo(),
      canUndo: current.can().undo(),
      code: current.isActive('code'),
      codeBlock: current.isActive('codeBlock'),
      h1: current.isActive('heading', { level: 1 }),
      h2: current.isActive('heading', { level: 2 }),
      h3: current.isActive('heading', { level: 3 }),
      highlight: current.isActive('highlight'),
      italic: current.isActive('italic'),
      link: current.isActive('link'),
      orderedList: current.isActive('orderedList'),
      strike: current.isActive('strike'),
      table: current.isActive('table'),
      taskList: current.isActive('taskList')
    })
  });

  const label = (key: string) => translate(`notes.editor.${key}`);

  return (
    <div
      className={'flex flex-wrap items-center gap-0.5 border-b border-border pb-2'}
      onMouseDown={event => event.preventDefault()}
      role={'toolbar'}
    >
      <ToolButton
        disabled={!state.canUndo}
        label={label('history.undo')}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 />
      </ToolButton>

      <ToolButton
        disabled={!state.canRedo}
        label={label('history.redo')}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 />
      </ToolButton>

      <ToolDivider />

      <ToolButton
        active={state.h1}
        label={label('blocks.heading1')}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 />
      </ToolButton>

      <ToolButton
        active={state.h2}
        label={label('blocks.heading2')}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 />
      </ToolButton>

      <ToolButton
        active={state.h3}
        label={label('blocks.heading3')}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 />
      </ToolButton>

      <ToolDivider />

      <ToolButton
        active={state.bold}
        label={label('marks.bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold />
      </ToolButton>

      <ToolButton
        active={state.italic}
        label={label('marks.italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic />
      </ToolButton>

      <ToolButton
        active={state.strike}
        label={label('marks.strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough />
      </ToolButton>

      <ToolButton
        active={state.code}
        label={label('marks.code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Code />
      </ToolButton>

      <ToolButton
        active={state.highlight}
        label={label('marks.highlight')}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
      >
        <Highlighter />
      </ToolButton>

      <LinkPopover editor={editor} onOpenChange={onLinkOpenChange} open={linkOpen}>
        <ToolButton active={state.link} label={label('marks.link')} onMouseDown={event => event.stopPropagation()}>
          <Link2 />
        </ToolButton>
      </LinkPopover>

      <ToolDivider />

      <ToolButton
        active={state.bulletList}
        label={label('blocks.bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List />
      </ToolButton>

      <ToolButton
        active={state.orderedList}
        label={label('blocks.orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered />
      </ToolButton>

      <ToolButton
        active={state.taskList}
        label={label('blocks.taskList')}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
      >
        <CheckSquare />
      </ToolButton>

      <ToolDivider />

      <ToolButton
        active={state.blockquote}
        label={label('blocks.quote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote />
      </ToolButton>

      <ToolButton
        active={state.codeBlock}
        label={label('blocks.codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <Code2 />
      </ToolButton>

      <ToolButton
        active={state.table}
        label={label('blocks.table')}
        onClick={() => editor.chain().focus().insertTable({ cols: 3, rows: 3, withHeaderRow: true }).run()}
      >
        <Table />
      </ToolButton>

      <ToolButton label={label('blocks.divider')} onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <Minus />
      </ToolButton>

      <ToolButton label={label('blocks.image')} onClick={onPickImage}>
        <ImageIcon />
      </ToolButton>
    </div>
  );
}
